import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  Clock, Send, Edit2, CreditCard, CheckCircle, FileText, AlertCircle,
  Plus, Trash2, RefreshCw, Download, X,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Traduction des actions d'audit en labels humains + icône/couleur
const ACTION_CONFIG = {
  invoice_reopened_for_edit: { label: 'Facture rouverte pour édition', icon: Edit2, color: 'bg-orange-100 text-orange-700' },
  invoice_resent: { label: 'Facture renvoyée', icon: Send, color: 'bg-orange-100 text-orange-700' },
  contract_invoice_sent: { label: 'Facture envoyée', icon: Send, color: 'bg-purple-100 text-purple-700' },
  contract_invoice_validated: { label: 'Facture validée', icon: CheckCircle, color: 'bg-blue-100 text-blue-700' },
  invoice_item_updated: { label: 'Ligne modifiée', icon: Edit2, color: 'bg-gray-100 text-gray-700' },
  invoice_item_added: { label: 'Ligne ajoutée', icon: Plus, color: 'bg-green-100 text-green-700' },
  invoice_item_removed: { label: 'Ligne supprimée', icon: Trash2, color: 'bg-red-100 text-red-700' },
  extra_item_added: { label: 'Extra ajouté', icon: Plus, color: 'bg-amber-100 text-amber-700' },
  payment_recorded: { label: 'Paiement enregistré', icon: CreditCard, color: 'bg-emerald-100 text-emerald-700' },
};

function formatDateTime(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleString('fr-FR', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return String(iso);
  }
}

export default function InvoiceHistoryModal({ open, onClose, invoice, onToast }) {
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState(null);
  const [downloadingVersion, setDownloadingVersion] = useState(null);

  const fetchHistory = useCallback(async () => {
    if (!invoice?.id) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/contract-invoices/${invoice.id}/history`);
      setHistory(res.data);
    } catch (e) {
      if (onToast) onToast({
        title: 'Erreur',
        description: e.response?.data?.detail || 'Impossible de charger l\'historique',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  }, [invoice?.id, onToast]);

  useEffect(() => {
    if (open && invoice?.id) fetchHistory();
  }, [open, invoice?.id, fetchHistory]);

  const handleDownloadVersion = async (version) => {
    setDownloadingVersion(version);
    try {
      const bust = Date.now();
      const res = await fetch(
        `${API}/contract-invoices/${invoice.id}/versions/${version}/pdf?t=${bust}`,
        { headers: { Authorization: 'Bearer cookie-auth', 'Cache-Control': 'no-cache' } }
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${invoice.invoiceNumber || invoice.id}_V${version}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      if (onToast) onToast({
        title: 'Erreur',
        description: `Téléchargement V${version} impossible`,
        variant: 'destructive',
      });
    } finally {
      setDownloadingVersion(null);
    }
  };

  if (!invoice) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="invoice-history-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Clock className="w-5 h-5 text-orange-600" />
            Historique — {invoice.invoiceNumber}
          </DialogTitle>
          <DialogDescription>
            Timeline des événements et versions PDF archivées · {history?.currentVersion
              ? `Version courante : V${history.currentVersion}`
              : ''}
          </DialogDescription>
        </DialogHeader>

        {loading && (
          <div className="py-12 text-center text-gray-500">
            <RefreshCw className="w-6 h-6 mx-auto animate-spin mb-2" />
            Chargement...
          </div>
        )}

        {!loading && history && (
          <div className="space-y-6">
            {/* ─── Versions PDF archivées ─── */}
            <section>
              <h3 className="text-sm font-semibold mb-3 flex items-center gap-2 text-gray-700">
                <FileText className="w-4 h-4" />
                Versions PDF ({history.pdfVersions?.length || 0})
              </h3>
              {(!history.pdfVersions || history.pdfVersions.length === 0) ? (
                <p className="text-xs text-gray-500 italic p-3 rounded bg-gray-50">
                  Aucune version PDF archivée. Les PDFs sont archivés automatiquement lors d'une modification post-envoi.
                </p>
              ) : (
                <ul className="space-y-2">
                  {history.pdfVersions.map((v, idx) => (
                    <li
                      key={`v${v.version}-${idx}`}
                      className={`flex items-center justify-between p-3 rounded-lg border transition ${
                        v.isCurrent
                          ? 'border-blue-300 bg-blue-50'
                          : 'border-gray-200 bg-white hover:bg-gray-50'
                      }`}
                      data-testid={`invoice-pdf-version-${v.version}`}
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <Badge className={v.isCurrent ? 'bg-blue-600' : 'bg-gray-400'}>
                          V{v.version}
                        </Badge>
                        <div className="min-w-0">
                          <div className="text-sm font-medium text-gray-900">
                            {v.isCurrent ? 'Version courante' : 'Version archivée'}
                          </div>
                          <div className="text-xs text-gray-500 truncate">
                            {v.sentAt ? `Envoyée le ${formatDateTime(v.sentAt)}` : 'Jamais envoyée'}
                          </div>
                        </div>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleDownloadVersion(v.version)}
                        disabled={downloadingVersion === v.version || !v.pdfPath}
                        data-testid={`download-version-${v.version}`}
                      >
                        {downloadingVersion === v.version ? (
                          <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                        ) : (
                          <>
                            <Download className="w-3.5 h-3.5 mr-1" />
                            PDF
                          </>
                        )}
                      </Button>
                    </li>
                  ))}
                </ul>
              )}
            </section>

            {/* ─── Timeline des événements ─── */}
            <section>
              <h3 className="text-sm font-semibold mb-3 flex items-center gap-2 text-gray-700">
                <Clock className="w-4 h-4" />
                Timeline d'audit ({history.events?.length || 0} événements)
              </h3>
              {(!history.events || history.events.length === 0) ? (
                <p className="text-xs text-gray-500 italic p-3 rounded bg-gray-50">
                  Aucun événement tracé pour cette facture.
                </p>
              ) : (
                <ol className="relative border-l-2 border-gray-200 pl-5 space-y-3">
                  {[...history.events].reverse().map((ev, idx) => {
                    const cfg = ACTION_CONFIG[ev.action] || {
                      label: ev.action,
                      icon: AlertCircle,
                      color: 'bg-gray-100 text-gray-700',
                    };
                    const Icon = cfg.icon;
                    return (
                      <li
                        key={`${ev.timestamp}-${idx}`}
                        className="relative"
                        data-testid={`timeline-event-${idx}`}
                      >
                        <span
                          className={`absolute -left-[30px] flex items-center justify-center w-6 h-6 rounded-full ring-4 ring-white ${cfg.color}`}
                        >
                          <Icon className="w-3 h-3" />
                        </span>
                        <div className="bg-white border border-gray-200 rounded-lg p-3">
                          <div className="flex items-start justify-between gap-2">
                            <div className="min-w-0">
                              <div className="text-sm font-medium text-gray-900">{cfg.label}</div>
                              <div className="text-xs text-gray-500 mt-0.5">
                                {formatDateTime(ev.timestamp)} · {ev.userName || 'Système'}
                              </div>
                            </div>
                          </div>
                          {ev.details && Object.keys(ev.details).length > 0 && (
                            <div className="mt-2 text-xs text-gray-600 space-y-0.5">
                              {ev.details.reason && (
                                <div><span className="font-medium">Motif :</span> « {ev.details.reason} »</div>
                              )}
                              {ev.details.newVersion && (
                                <div>
                                  Version {ev.details.previousVersion} → <strong>V{ev.details.newVersion}</strong>
                                </div>
                              )}
                              {ev.details.before && ev.details.after && (
                                <div className="font-mono text-[10px] bg-gray-50 p-1.5 rounded">
                                  <div>Avant : {JSON.stringify(ev.details.before)}</div>
                                  <div>Après : {JSON.stringify(ev.details.after)}</div>
                                </div>
                              )}
                              {ev.details.amount && (
                                <div>
                                  <span className="font-medium">Montant :</span> {new Intl.NumberFormat('fr-FR').format(ev.details.amount)} FCFA
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </li>
                    );
                  })}
                </ol>
              )}
            </section>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="close-history-modal">
            <X className="w-4 h-4 mr-1" />
            Fermer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
